import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Package, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Public order lookup page (`/track`).
 *
 * Customer enters their order number + first name, we resolve it to a
 * tracking_token via `POST /api/tracking/public/lookup`, then redirect
 * to `/track/<token>` which renders the existing OrderTrackingPage.
 *
 * The tokenized URL (`/track/:token`) keeps working unchanged — this is
 * a strictly additive entry-point for customers who lost their link.
 */
const TrackingLookupPage = () => {
  const navigate = useNavigate();
  const [orderNumber, setOrderNumber] = useState('');
  const [firstName, setFirstName] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');

    const trimmedOrder = orderNumber.trim();
    const trimmedName = firstName.trim();
    if (!trimmedOrder || !trimmedName) {
      setErrorMsg('Please enter both your order number and first name.');
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(`${API_BASE_URL}/tracking/public/lookup`, {
        order_number: trimmedOrder,
        first_name: trimmedName,
      });
      if (data?.tracking_token) {
        navigate(`/track/${data.tracking_token}`);
      } else {
        setErrorMsg("We couldn't find that order. Please check the details and try again.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setErrorMsg(detail || "We couldn't find that order. Please check the details and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-10"
      style={{
        background:
          'radial-gradient(ellipse at top, rgba(99,102,241,0.15), transparent 60%), radial-gradient(ellipse at bottom right, rgba(245,158,11,0.10), transparent 60%), #0b1020',
      }}
      data-testid="tracking-lookup-page"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm mb-4">
            <Package className="w-7 h-7 text-amber-400" />
          </div>
          <h1 className="text-3xl font-semibold text-white tracking-tight">
            Track your order
          </h1>
          <p className="text-sm text-slate-400 mt-2">
            Enter your order number and first name to view live tracking.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white/[0.04] backdrop-blur-md border border-white/10 rounded-2xl p-6 shadow-2xl"
          data-testid="tracking-lookup-form"
        >
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-slate-300 uppercase tracking-wider">
                Order number
              </label>
              <Input
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                placeholder="e.g. #1042"
                autoComplete="off"
                className="mt-2 bg-slate-900/50 border-white/10 text-white placeholder:text-slate-500 h-11"
                data-testid="lookup-order-number-input"
                disabled={loading}
              />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-300 uppercase tracking-wider">
                First name
              </label>
              <Input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="As on your order"
                autoComplete="given-name"
                className="mt-2 bg-slate-900/50 border-white/10 text-white placeholder:text-slate-500 h-11"
                data-testid="lookup-first-name-input"
                disabled={loading}
              />
            </div>

            {errorMsg ? (
              <div
                className="text-sm text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2"
                data-testid="lookup-error-message"
              >
                {errorMsg}
              </div>
            ) : null}

            <Button
              type="submit"
              className="w-full h-11 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
              disabled={loading}
              data-testid="lookup-submit-button"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Looking up…
                </>
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" /> Track order
                </>
              )}
            </Button>
          </div>

          <p className="text-xs text-slate-500 text-center mt-5">
            Have a tracking link? Open it directly to skip this step.
          </p>
        </form>
      </div>
    </div>
  );
};

export default TrackingLookupPage;
